/**
 * OrderBlockPrimitive (VOB) — time + price bounded rectangles split into a top
 * "Sell %" half and a bottom "Buy %" half, with right-aligned labels. Modeled on
 * SrBandPrimitive's canvas approach (timeToCoordinate × priceToCoordinate,
 * bitmap space, zOrder 'top' so the translucent fills wash over the candles like
 * the TradingView original while text stays crisp).
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface ObBox {
  leftTime: number;
  rightTime: number;
  top: number;
  bottom: number;
  splitPrice: number;
  /** Sell (top) half fill — rgba, opacity baked in. */
  topFill: string;
  /** Buy (bottom) half fill — rgba, opacity baked in. */
  botFill: string;
  /** Border + split-line colour — rgba, opacity baked in. */
  border: string;
  topText: string;   // e.g. "Sell: 35%"
  botText: string;   // e.g. "Buy: 65%"
  textColor: string;
}

class ObRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly boxes: ObBox[],
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
    private readonly connectorColor?: string,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.boxes.length) return;
    const ts = this.chart.timeScale();
    const series = this.series;

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const W = bitmapSize.width;
      const fontPx = Math.round(10 * Math.min(hpr, vpr));
      const padX = 5 * hpr;

      // Collect each rendered box's centre so we can join consecutive order blocks
      // with a connector line (so the sequence reads continuously, not as detached
      // rectangles). Ordered by leftTime.
      const centres: Array<{ t: number; x: number; y: number }> = [];

      for (const b of this.boxes) {
        const xlRaw = ts.timeToCoordinate(b.leftTime as Time);
        const xrRaw = ts.timeToCoordinate(b.rightTime as Time);
        if (xlRaw == null && xrRaw == null) continue;
        let xl = xlRaw == null ? 0 : Number(xlRaw) * hpr;
        let xr = xrRaw == null ? W : Number(xrRaw) * hpr;
        xl = Math.max(0, Math.min(W, xl));
        xr = Math.max(0, Math.min(W, xr));
        if (xr - xl < 1) continue;

        const yTopR = series.priceToCoordinate(b.top);
        const ySplR = series.priceToCoordinate(b.splitPrice);
        const yBotR = series.priceToCoordinate(b.bottom);
        if (yTopR == null || ySplR == null || yBotR == null) continue;
        const yTop = Number(yTopR) * vpr;
        const ySpl = Number(ySplR) * vpr;
        const yBot = Number(yBotR) * vpr;
        const yHi = Math.min(yTop, yBot);
        const yLo = Math.max(yTop, yBot);
        centres.push({ t: b.leftTime, x: (xl + xr) / 2, y: (yTop + yBot) / 2 });

        // Sell (top) half + Buy (bottom) half fills.
        ctx.fillStyle = b.topFill;
        ctx.fillRect(xl, Math.min(yTop, ySpl), xr - xl, Math.abs(ySpl - yTop));
        ctx.fillStyle = b.botFill;
        ctx.fillRect(xl, Math.min(ySpl, yBot), xr - xl, Math.abs(yBot - ySpl));

        // Outer border + subtle split line.
        ctx.lineWidth = Math.max(1, Math.round(Math.min(hpr, vpr)));
        ctx.strokeStyle = b.border;
        ctx.setLineDash([]);
        ctx.strokeRect(xl, yHi, xr - xl, yLo - yHi);
        ctx.beginPath();
        ctx.moveTo(xl, ySpl);
        ctx.lineTo(xr, ySpl);
        ctx.stroke();

        // Right-aligned, vertically-centred labels (skip when a half is too small).
        ctx.font = `${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = b.textColor;
        const tx = xr - padX;
        const drawLabel = (text: string, yA: number, yB: number) => {
          const h = Math.abs(yB - yA);
          if (h < fontPx + 4 * vpr) return;
          const w = ctx.measureText(text).width;
          if (w + 2 * padX > xr - xl) return;
          ctx.fillText(text, tx, (yA + yB) / 2);
        };
        drawLabel(b.topText, yTop, ySpl);
        drawLabel(b.botText, ySpl, yBot);
      }

      // Connector line joining consecutive order-block centres.
      if (this.connectorColor && centres.length >= 2) {
        centres.sort((a, b) => a.t - b.t);
        ctx.strokeStyle = this.connectorColor;
        ctx.lineWidth = Math.max(1, Math.round(Math.min(hpr, vpr)));
        ctx.setLineDash([4 * hpr, 4 * hpr]);
        ctx.beginPath();
        ctx.moveTo(centres[0].x, centres[0].y);
        for (let i = 1; i < centres.length; i++) ctx.lineTo(centres[i].x, centres[i].y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.restore();
    });
  }
}

class ObPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: OrderBlockPrimitive) {}
  zOrder() { return 'top' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    const series = this.parent.series;
    if (!chart || !series) return null;
    return new ObRenderer(this.parent.boxes, chart, series, this.parent.connectorColor);
  }
}

export class OrderBlockPrimitive implements ISeriesPrimitive<Time> {
  boxes: ObBox[] = [];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  connectorColor?: string;
  private readonly _paneViews: ObPaneView[];

  constructor(boxes: ObBox[], opts?: { connectorColor?: string }) {
    this.boxes = boxes;
    this.connectorColor = opts?.connectorColor;
    this._paneViews = [new ObPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* renderer reads this.boxes fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(boxes: ObBox[]): void {
    this.boxes = boxes;
    this.chart?.applyOptions({});
  }
}
